import React, { useCallback, useEffect, useRef, useState } from "react";
import "./PresentationStudioPage.css";
import { apiGet, apiPost, apiPut, createJobPoller, downloadFile, forgetRememberedJob, readRememberedJob, rememberJob } from "../apiClient";
import { ACTIVE_JOB_STATUSES, VIEW_SCOPE_WORKSPACE } from "../domainCatalog";

const DEFAULT_THEME = { primary_color: "#4f46e5", accent_color: "#14b8a6", background_color: "#081121", text_color: "#f8fafc", font_family: "Arial", pattern: "gradient", logo_data_url: "" };
const EMPTY = { title: "Sprint status report", sprint_name: "", slides: [], theme: DEFAULT_THEME };
const PRESENTATION_GENERATION_JOB_KEY = "taskit.presentationGenerationJob";

const PresentationStudioPage = ({ token, model, scope = "workstream", onUsageChange }) => {
  const logoInput = useRef(null);
  const presentationView = useRef(null);
  const [deck, setDeck] = useState(EMPTY);
  const [saved, setSaved] = useState(EMPTY);
  const [selected, setSelected] = useState(0);
  const [instruction, setInstruction] = useState("");
  const [chat, setChat] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [generationJob, setGenerationJob] = useState(null);
  const [generationSource, setGenerationSource] = useState("jira");
  const slide = deck.slides[selected];
  const dirty = JSON.stringify(deck) !== JSON.stringify(saved);
  const workspaceView = scope === VIEW_SCOPE_WORKSPACE;
  const activeGenerationJobId = generationJob && ACTIVE_JOB_STATUSES.includes(generationJob.status)
    ? generationJob.id
    : null;

  const load = useCallback(async () => {
    try {
      const data = await apiGet(`/presentation${scope === VIEW_SCOPE_WORKSPACE ? "?scope=workspace" : ""}`, { token });
      if (data) {
        const next = editable(data);
        setDeck(next);
        setSaved(next);
      }
    } catch (error) { setMessage(error.message); }
  }, [token, scope]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const remembered = readRememberedJob(PRESENTATION_GENERATION_JOB_KEY);
    if (!remembered?.id) return;
    setGenerationJob(remembered);
    setGenerationSource(remembered.source || "jira");
    setLoading(true);
    setMessage("Resuming presentation generation job...");
  }, []);
  useEffect(() => {
    if (!presenting) return undefined;
    const navigate = (event) => {
      if (isTextInput(event.target)) return;
      if (event.key === "Escape") setPresenting(false);
      if (event.key === "ArrowRight" || event.key === " ") {
        event.preventDefault();
        setSelected((value) => Math.min(value + 1, deck.slides.length - 1));
      }
      if (event.key === "ArrowLeft") setSelected((value) => Math.max(value - 1, 0));
    };
    window.addEventListener("keydown", navigate);
    return () => window.removeEventListener("keydown", navigate);
  }, [presenting, deck.slides.length]);
  useEffect(() => {
    const syncFullscreen = () => {
      if (!document.fullscreenElement) setPresenting(false);
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  const request = async (operation, workingMessage) => {
    setLoading(true);
    setMessage(workingMessage);
    try {
      return await operation();
    } finally { setLoading(false); }
  };

  const loadGenerationJob = useCallback(async (jobId) => {
    if (!jobId) return null;
    try {
      const data = await apiGet(`/jobs/${jobId}`, { token });
      setGenerationJob(data);
      setMessage(jobStatusMessage(data, "Presentation generation"));
      if (!ACTIVE_JOB_STATUSES.includes(data.status)) {
        forgetRememberedJob(PRESENTATION_GENERATION_JOB_KEY);
        setLoading(false);
        if (data.status === "completed") {
          const next = editable(data.result_payload);
          setDeck(next); setSaved(next); setSelected(0);
          setMessage(generationSource === "taskit_board" ? "Presentation generated from the latest Taskit Board." : "Presentation generated from the latest project and sprint data.");
          onUsageChange?.();
        } else if (data.status === "failed") {
          setMessage(data.error_message || "Presentation generation failed.");
        }
      }
      return data;
    } catch (error) {
      forgetRememberedJob(PRESENTATION_GENERATION_JOB_KEY);
      setLoading(false);
      setMessage(error.message || "Error loading presentation generation job.");
      return null;
    }
  }, [generationSource, onUsageChange, token]);

  useEffect(() => {
    if (!activeGenerationJobId) return undefined;
    return createJobPoller(
      () => loadGenerationJob(activeGenerationJobId),
      { shouldContinue: (job) => Boolean(job && ACTIVE_JOB_STATUSES.includes(job.status)), immediate: true }
    );
  }, [activeGenerationJobId, loadGenerationJob]);

  const generate = async (source = "jira") => {
    try {
      const board = source === "taskit_board";
      setGenerationSource(source);
      const job = await request(() => apiPost(board ? "/presentation/generate/board" : "/presentation/generate", undefined, { token, model }), `Queueing ${board ? "Taskit Board" : "Jira"} presentation generation...`);
      setGenerationJob(job);
      rememberJob(PRESENTATION_GENERATION_JOB_KEY, { id: job.id, status: job.status, source });
      setLoading(true);
      setMessage("Presentation generation queued.");
    } catch (error) { setMessage(error.message); }
  };

  const save = async () => {
    try {
      const data = await request(() => apiPut("/presentation", deck, { token }), "Saving presentation...");
      const next = editable(data);
      setDeck(next); setSaved(next); setMessage("Latest presentation saved.");
    } catch (error) { setMessage(error.message); }
  };

  const askAssistant = async () => {
    if (!instruction.trim()) return;
    const text = instruction.trim();
    setInstruction(""); setChat((items) => [...items, { role: "user", content: text }]);
    try {
      const data = await request(() => apiPost("/presentation/assistant", { presentation: deck, instruction: text }, { token, model }), "The assistant is revising the presentation...");
      setDeck(editable(data.revised_presentation));
      setChat((items) => [...items, { role: "assistant", content: data.message }]);
      setMessage("Assistant changes applied as a draft.");
      onUsageChange?.();
    } catch (error) { setChat((items) => [...items, { role: "assistant", content: error.message }]); }
  };

  const downloadPdf = async () => {
    if (dirty) return setMessage("Save the latest presentation before downloading the PDF.");
    setLoading(true);
    try {
      await downloadFile("/presentation/export/pdf", { token, filename: "sprint-status.pdf" });
      setMessage("PDF download ready.");
    } catch (error) { setMessage(error.message); } finally { setLoading(false); }
  };

  const updateSlide = (field, value) => setDeck((current) => ({ ...current, slides: current.slides.map((item, index) => index === selected ? { ...item, [field]: value } : item) }));
  const updateTheme = (field, value) => setDeck((current) => ({ ...current, theme: { ...current.theme, [field]: value } }));
  const addSlide = () => {
    setDeck((current) => ({ ...current, slides: [...current.slides, { id: `slide-${Date.now()}`, layout: "summary", title: "New slide", subtitle: "", body: "", bullets: [], tasks: [], metrics: [] }] }));
    setSelected(deck.slides.length);
  };
  const deleteSlide = () => { setDeck((current) => ({ ...current, slides: current.slides.filter((_, index) => index !== selected) })); setSelected(Math.max(selected - 1, 0)); };
  const uploadLogo = (file) => { if (file) { const reader = new FileReader(); reader.onload = () => updateTheme("logo_data_url", reader.result); reader.readAsDataURL(file); } };
  const startPresentation = () => {
    setPresenting(true);
    window.setTimeout(() => presentationView.current?.requestFullscreen?.().catch?.(() => {}), 0);
  };
  const exitPresentation = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    setPresenting(false);
  };

  return <div className="presentation-studio-page">
    <header className="presentation-header"><div><h1>Presentation Studio</h1><p>{workspaceView ? "Consolidated presentation from all workstreams." : "Generate an editable status report from Jira or from the latest Taskit Board."}</p></div><div className="presentation-actions">
      <button onClick={() => generate("jira")} disabled={loading || workspaceView}>{loading ? "Working..." : "Generate current status"}</button>
      <button onClick={() => generate("taskit_board")} disabled={loading || workspaceView}>{loading ? "Working..." : "Generate from Taskit Board"}</button>
      <button className="presentation-mode-button" onClick={startPresentation} disabled={!deck.slides.length}>Presentation</button>
      <button onClick={downloadPdf} disabled={loading || workspaceView || !deck.slides.length}>Download PDF</button>
      <button className="primary" onClick={save} disabled={loading || workspaceView || !dirty}>Save latest version</button>
    </div></header>
    {message && <div className="presentation-message">{message}</div>}
    <div className="presentation-workspace">
      <aside className="slide-sidebar"><div className="slide-sidebar-title"><strong>Slides</strong><button onClick={addSlide} disabled={workspaceView}>+ Add</button></div>
        {deck.slides.map((item, index) => <button key={item.id} className={`slide-thumbnail ${selected === index ? "selected" : ""}`} onClick={() => setSelected(index)}><span>{index + 1}</span><MiniSlide slide={item} theme={deck.theme} /><strong>{item.title}</strong></button>)}
      </aside>
      <main className="presentation-canvas-panel">
        <div className="presentation-theme-bar">
          <label>Primary<input type="color" value={deck.theme.primary_color} disabled={workspaceView} onChange={(e) => updateTheme("primary_color", e.target.value)} /></label>
          <label>Accent<input type="color" value={deck.theme.accent_color} disabled={workspaceView} onChange={(e) => updateTheme("accent_color", e.target.value)} /></label>
          <label>Background<input type="color" value={deck.theme.background_color} disabled={workspaceView} onChange={(e) => updateTheme("background_color", e.target.value)} /></label>
          <label>Font<select value={deck.theme.font_family} disabled={workspaceView} onChange={(e) => updateTheme("font_family", e.target.value)}><option>Arial</option><option>Georgia</option><option>Verdana</option><option>Trebuchet MS</option></select></label>
          <label>Pattern<select value={deck.theme.pattern} disabled={workspaceView} onChange={(e) => updateTheme("pattern", e.target.value)}><option value="gradient">Gradient</option><option value="grid">Grid</option><option value="circles">Circles</option><option value="none">None</option></select></label>
          <input ref={logoInput} hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => uploadLogo(e.target.files?.[0])} /><button onClick={() => logoInput.current?.click()} disabled={workspaceView}>{deck.theme.logo_data_url ? "Replace logo" : "Add logo"}</button>
        </div>
        {!slide ? <div className="presentation-empty">Generate a sprint report or add your first slide.</div> : <><Slide slide={slide} theme={deck.theme} /><div className="slide-editor">
          <div className="slide-editor-heading"><strong>{workspaceView ? "View slide" : "Edit slide"} {selected + 1}</strong><button onClick={deleteSlide} disabled={workspaceView}>Delete slide</button></div>
          <label>Layout<select value={slide.layout} disabled={workspaceView} onChange={(e) => updateSlide("layout", e.target.value)}><option value="title">Title</option><option value="summary">Summary</option><option value="metrics">Metrics</option><option value="work">Work list</option><option value="next_steps">Next steps</option></select></label>
          <label>Title<input value={slide.title} disabled={workspaceView} onChange={(e) => updateSlide("title", e.target.value)} /></label><label>Subtitle<input value={slide.subtitle} disabled={workspaceView} onChange={(e) => updateSlide("subtitle", e.target.value)} /></label>
          <label>Body<textarea rows="3" value={slide.body} disabled={workspaceView} onChange={(e) => updateSlide("body", e.target.value)} /></label><label>Bullets<textarea rows="5" value={slide.bullets.join("\n")} disabled={workspaceView} onChange={(e) => updateSlide("bullets", editBullets(e.target.value))} /></label>
          <label>Metrics (label: value)<textarea rows="4" value={metricsText(slide.metrics)} disabled={workspaceView} onChange={(e) => updateSlide("metrics", parseMetrics(e.target.value))} /></label>
          <label>Tasks (key | title | status | assignee | complexity)<textarea rows="6" value={tasksText(slide.tasks)} disabled={workspaceView} onChange={(e) => updateSlide("tasks", parseTasks(e.target.value))} /></label>
        </div></>}
      </main>
      <aside className="presentation-assistant"><h2>Presentation assistant</h2><div className="presentation-chat">{!chat.length && <p>Ask the assistant to sharpen the summary, emphasize risks, reorganize slides, or clarify next steps.</p>}{chat.map((item, index) => <div key={index} className={`presentation-chat-message ${item.role}`}>{item.content}</div>)}</div><textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="How should the presentation change?" /><button onClick={askAssistant} disabled={loading || workspaceView || !instruction.trim() || !deck.slides.length}>Send to assistant</button></aside>
    </div>
    {presenting && slide && <div ref={presentationView} className="presentation-fullscreen"><Slide slide={slide} theme={deck.theme} fullscreen /><button className="presentation-exit" onClick={exitPresentation}>Exit presentation</button><div className="presentation-fullscreen-help">← → navigate · Esc to exit · {selected + 1}/{deck.slides.length}</div></div>}
  </div>;
};

const Slide = ({ slide, theme, fullscreen }) => <section className={`presentation-slide pattern-${theme.pattern} ${fullscreen ? "fullscreen-slide" : ""}`} style={{ "--primary": theme.primary_color, "--accent": theme.accent_color, "--background": theme.background_color, "--text": theme.text_color, fontFamily: theme.font_family }}>
  {theme.logo_data_url && <img className="presentation-logo" src={theme.logo_data_url} alt="Presentation logo" />}<div className="slide-content"><span className="slide-kicker">{slide.layout.replace("_", " ")}</span><h2>{slide.title}</h2>{slide.subtitle && <h3>{slide.subtitle}</h3>}{slide.body && <p className="slide-body">{slide.body}</p>}
  {!!slide.metrics.length && <div className="slide-metrics">{slide.metrics.map((metric, index) => <div key={`${metric.label}-${index}`}><strong>{metric.value}</strong><span>{metric.label}</span></div>)}</div>}{!!slide.bullets.length && <ul>{slide.bullets.map((bullet, index) => <li key={index}>{bullet}</li>)}</ul>}
  {!!slide.tasks.length && <div className="slide-tasks">{slide.tasks.map((task, index) => <div key={`${task.key}-${index}`}><strong>{task.key}</strong><span>{task.title}</span><em>{task.status}{task.complexity ? ` · ${task.complexity} pts` : ""}</em></div>)}</div>}</div></section>;
const MiniSlide = ({ slide, theme }) => <div className="mini-slide" style={{ background: theme.background_color, color: theme.text_color, borderColor: theme.primary_color }}><b>{slide.title}</b><span>{slide.bullets[0] || slide.body || slide.subtitle}</span></div>;
const editable = (data) => ({ title: data.title, sprint_name: data.sprint_name, slides: (data.slides || []).map((slide) => ({ ...slide, bullets: normalizeBullets(slide.bullets || []), metrics: Array.isArray(slide.metrics) ? slide.metrics : Object.entries(slide.metrics || {}).map(([label, value]) => ({ label, value })) })), theme: { ...DEFAULT_THEME, ...data.theme } });
const nonEmptyLines = (value) => value.split("\n").filter((line) => line.trim());
const normalizeBullets = (bullets) => bullets.flatMap((bullet) => String(bullet).replace(/\r\n/g, "\n").replace(/ [•●▪] /g, "\n").split("\n")).map((bullet) => bullet.replace(/^[•●▪-]\s*/, "").trim()).filter(Boolean);
const editBullets = (value) => value.replace(/\r\n/g, "\n").split("\n").map((line) => line.replace(/^[•●▪-]\s*/, ""));
const metricsText = (metrics) => metrics.map((metric) => `${metric.label}: ${metric.value}`).join("\n");
const parseMetrics = (value) => nonEmptyLines(value).map((line) => {
  const [label, ...rest] = line.split(":");
  return { label: label.trim(), value: rest.join(":").replace(/^\s+/, "") };
}).filter((metric) => metric.label && metric.value);
const tasksText = (tasks) => tasks.map((task) => [task.key, task.title, task.status, task.assignee, task.complexity].join(" | ")).join("\n");
const parseTasks = (value) => nonEmptyLines(value).map((line) => {
  const [key = "", title = "", status = "", assignee = "", complexity = ""] = line.split("|").map((item) => item.replace(/^\s+/, ""));
  return { key, title, status, assignee, complexity };
}).filter((task) => task.title);
const jobStatusMessage = (job, label) => {
  if (job.status === "completed") return `${label} completed.`;
  if (job.status === "failed") return job.error_message || `${label} failed.`;
  return `${label}: ${job.current_step || "Queued"} (${job.progress || 0}%).`;
};
const isTextInput = (target) => ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName) || target?.isContentEditable;
export default PresentationStudioPage;
